// ERDEditor/ScaffoldPanel.jsx
/**
 * ScaffoldPanel
 * =============
 * Renders one scaffold stage at a time.
 *
 * Flow:
 *   mount / stageId change → /scaffold/start (display phase)
 *     → show questions
 *   student submits answers → /scaffold/respond (output phase)
 *     → show adaptive excerpt + next options
 *   "Revise my ERD"       → onRevise()
 *   "I still need more help" → advance to next stage
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { scaffoldStart, scaffoldRespond, scaffoldQA } from '../../utils/api/ScaffoldApi';

function SimpleMarkdown({ children }) {
  if (!children) return null;
  return (
    <div>
      {String(children).split('\n').map((line, i) => {
        if (line.startsWith('### ')) return <h4 key={i} style={{ margin: '8px 0 4px', fontSize: 13, fontWeight: 700 }}>{line.slice(4)}</h4>;
        if (line.startsWith('## '))  return <h3 key={i} style={{ margin: '8px 0 4px', fontSize: 14, fontWeight: 700 }}>{line.slice(3)}</h3>;
        if (line.startsWith('# '))   return <h2 key={i} style={{ margin: '8px 0 4px', fontSize: 15, fontWeight: 700 }}>{line.slice(2)}</h2>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <div key={i} style={{ paddingLeft: 14, margin: '2px 0' }}>• {fmt(line.slice(2))}</div>;
        if (/^\d+\.\s/.test(line)) return <div key={i} style={{ paddingLeft: 14, margin: '2px 0' }}>{fmt(line)}</div>;
        if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
        return <p key={i} style={{ margin: '3px 0', lineHeight: 1.55 }}>{fmt(line)}</p>;
      })}
    </div>
  );
}
function fmt(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2,-2)}</strong> : p
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MCQQuestion({ question, answer, onChange, disabled }) {
  return (
    <div style={styles.questionCard}>
      <p style={styles.questionText}>{question.question}</p>
      <div style={styles.optionsList}>
        {Object.entries(question.options || {}).map(([key, text]) => (
          <label key={key} style={styles.optionLabel}>
            <input
              type="radio"
              name={question.resource_id}
              value={key}
              checked={answer === key}
              onChange={() => onChange(question.resource_id, key)}
              disabled={disabled}
              style={{ marginRight: 8 }}
            />
            <span><strong>{key}.</strong> {text}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function OpenEndedQuestion({ question, answer, onChange, disabled }) {
  return (
    <div style={styles.questionCard}>
      <p style={styles.questionText}>{question.question}</p>
      <textarea
        value={answer || ''}
        onChange={e => onChange(question.resource_id, e.target.value)}
        disabled={disabled}
        placeholder="Type your answer here…"
        rows={4}
        style={styles.openEndedTextarea}
      />
    </div>
  );
}

function CorrectnessIndicator({ resourceId, correctness }) {
  if (!correctness) return null;
  const isCorrect = correctness[resourceId];
  if (isCorrect === undefined) return null;
  return (
    <span style={{ color: isCorrect ? '#22c55e' : '#ef4444', fontWeight: 600, marginLeft: 4 }}>
      {isCorrect ? '✓ Correct' : '✗ Not quite'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ScaffoldPanel({
  erdState,
  sectionId,
  environmentId,
  getClassContext,
  initialStageId = 'stage_1',
  onRevise,
  onStageComplete,   // () => void — called after each scaffold/respond succeeds
}) {
  const [phase, setPhase]               = useState('loading');  // loading | display | no_questions | auto_generating | submitted | qa_chat | error
  const [stageId, setStageId]           = useState(initialStageId);
  const [displayMessage, setDisplayMessage] = useState('');
  const [questions, setQuestions]       = useState([]);
  const [selectedIds, setSelectedIds]   = useState([]);
  const [answers, setAnswers]           = useState({});          // { resource_id: 'A' }
  const [correctness, setCorrectness]   = useState({});
  const [excerpt, setExcerpt]           = useState('');
  const [nextOptions, setNextOptions]   = useState([]);
  const [nextStageId, setNextStageId]   = useState(null);
  const [error, setError]               = useState('');
  const [submitting, setSubmitting]     = useState(false);

  // Q&A chat state (qa_interaction stages)
  const [chatHistory, setChatHistory]   = useState([]);   // [{role:'student'|'ai', text:''}]
  const [qaInput, setQaInput]           = useState('');
  const [qaSending, setQaSending]       = useState(false);
  const chatEndRef = useRef(null);
  // Ref so fireOutputPhase always reads the live questions list (useCallback deps don't include questions)
  const questionsRef = useRef(questions);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  // Session log — accumulates one entry per completed stage for the summary view
  // MCQ entry:  { stageId, type:'mcq', questions, answers, correctness, excerpt }
  // Q&A entry:  { stageId, type:'qa_interaction', chatHistory }
  const [sessionLog, setSessionLog]     = useState([]);

  // Build component_context from erdState
  const buildContext = useCallback(() => {
    const feedbackText = (erdState?.feedbackItems || [])
      .filter(i => i.type === 'text')
      .map(i => i.content)
      .join('\n');
    return {
      contextualized_feedback: feedbackText,
      has_errors: erdState?.hasErrors !== undefined ? erdState.hasErrors : true,
      error_types: erdState?.errorTypes || [],
      relevant_erd_part: JSON.stringify(erdState?.erdData || {}),
      relevant_requirement_statement: JSON.stringify(erdState?.relevantStatements || []),
    };
  }, [erdState]);

  const getClassId = useCallback(() => {
    const ctx = getClassContext ? getClassContext() : null;
    return ctx?.classId || ctx?.class_id || null;
  }, [getClassContext]);

  // Scroll chat to bottom whenever history grows
  useEffect(() => {
    if (phase === 'qa_chat') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, phase]);

  // Q&A: send one turn
  const handleQASend = useCallback(async () => {
    const q = qaInput.trim();
    if (!q || qaSending) return;
    const classId = getClassId();
    if (!classId) return;

    const nextHistory = [...chatHistory, { role: 'student', text: q }];
    setChatHistory(nextHistory);
    setQaInput('');
    setQaSending(true);

    try {
      const data = await scaffoldQA({
        classId,
        sectionId,
        environmentId,
        component: 'ERD',
        stageId,
        feedbackId: erdState?.feedbackId,
        componentContext: buildContext(),
        question: q,
        chatHistory: chatHistory,   // history before this turn
      });
      if (data.success) {
        setChatHistory(prev => [...prev, { role: 'ai', text: data.answer }]);
      } else {
        setChatHistory(prev => [...prev, { role: 'ai', text: `Error: ${data.error || 'Could not get answer.'}` }]);
      }
    } catch (err) {
      setChatHistory(prev => [...prev, { role: 'ai', text: 'Network error. Please try again.' }]);
    } finally {
      setQaSending(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qaInput, qaSending, chatHistory, stageId, sectionId, erdState?.feedbackId]);

  // Q&A: student clicks "Revise my ERD" — save QA completion, capture to session log, show summary
  const handleQARevise = useCallback(async () => {
    const classId = getClassId();
    if (!classId) return;
    setSubmitting(true);
    const capturedChat    = chatHistory;
    const capturedStageId = stageId;
    try {
      const data = await scaffoldRespond({
        classId,
        sectionId,
        environmentId,
        component: 'ERD',
        stageId,
        feedbackId: erdState?.feedbackId,
        componentContext: buildContext(),
        selectedQuestionIds: [],
        studentAnswers: { qa_turns: chatHistory.filter(t => t.role === 'student').length },
      });
      if (data.success) {
        setSessionLog(prev => [...prev, {
          stageId: capturedStageId,
          type: 'qa_interaction',
          chatHistory: capturedChat,
        }]);
        onStageComplete?.();
        setPhase('session_summary');
      } else {
        setError(data.error || 'Could not save Q&A session.');
        setPhase('error');
      }
    } catch (err) {
      setError('Network error.');
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId, sectionId, erdState?.feedbackId, chatHistory]);

  // ------------------------------------------------------------------
  // Display phase: fetch questions for current stage
  // ------------------------------------------------------------------
  useEffect(() => {
    const classId = getClassId();
    if (!classId || !sectionId || !erdState?.feedbackId) return;

    setPhase('loading');
    setAnswers({});
    setCorrectness({});
    setExcerpt('');

    scaffoldStart({
      classId,
      sectionId,
      environmentId,
      component: 'ERD',
      stageId,
      feedbackId: erdState.feedbackId,
      componentContext: buildContext(),
    }).then(data => {
      if (!data.success) {
        setError(data.error || 'Could not load scaffold questions.');
        setPhase('error');
        return;
      }

      // Condition stage: LLM resolved which stage to run next (or null = no scaffold)
      if (data.is_condition) {
        if (data.next_stage_id) {
          console.info('[ScaffoldPanel] condition resolved → stage:', data.next_stage_id);
          setStageId(data.next_stage_id);   // re-triggers this useEffect
        } else {
          console.info('[ScaffoldPanel] condition resolved → no scaffold needed');
          setPhase('no_scaffold_needed');
        }
        return;
      }

      // Q&A interaction stage: show chat UI
      if (data.is_qa_stage) {
        console.info('[ScaffoldPanel] qa_interaction stage — switching to qa_chat phase');
        setDisplayMessage(data.display_message || '');
        setChatHistory([]);
        setQaInput('');
        setPhase('qa_chat');
        return;
      }

      const qs = data.questions || [];
      const ids = data.selected_question_ids || [];
      setDisplayMessage(data.display_message || '');
      setQuestions(qs);
      setSelectedIds(ids);

      if (qs.length === 0 && data.student_input_required === false) {
        // Feedback-only stage (e.g. stage 3) — auto-fire output phase immediately
        console.info('[ScaffoldPanel] no questions + no input required — auto-generating feedback');
        setPhase('auto_generating');
        fireOutputPhase(classId, ids, {});
      } else if (qs.length === 0) {
        console.warn('[ScaffoldPanel] no questions for this stage — showing navigation options only');
        setPhase('no_questions');
      } else {
        setPhase('display');
      }
    }).catch(err => {
      console.error('[ScaffoldPanel] start error:', err);
      setError('Network error loading scaffold.');
      setPhase('error');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId, erdState?.feedbackId]);

  // ------------------------------------------------------------------
  // Output phase: shared logic used by both handleSubmit and auto-fire
  // ------------------------------------------------------------------
  const fireOutputPhase = useCallback(async (classId, questionIds, studentAnswers) => {
    try {
      const questionTexts = {};
      questionsRef.current.forEach(q => { if (q.resource_id) questionTexts[q.resource_id] = q.question || ''; });

      const data = await scaffoldRespond({
        classId,
        sectionId,
        environmentId,
        component: 'ERD',
        stageId,
        feedbackId: erdState?.feedbackId,
        componentContext: buildContext(),
        selectedQuestionIds: questionIds,
        studentAnswers,
        questionTexts,
      });

      if (data.success) {
        setCorrectness(data.correctness || {});
        setExcerpt(data.concept_excerpt || '');
        setNextOptions(Array.isArray(data.next_options) ? data.next_options : ['Revise my ERD', 'I still need more help']);
        setNextStageId(data.next_stage_id || null);
        setPhase('submitted');
        onStageComplete?.();
      } else {
        setError(data.error || 'Could not generate feedback.');
        setPhase('error');
      }
    } catch (err) {
      console.error('[ScaffoldPanel] output phase error:', err);
      setError('Network error.');
      setPhase('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId, sectionId, erdState?.feedbackId]);

  const handleSubmit = async () => {
    const classId = getClassId();
    if (!classId) return;
    setSubmitting(true);
    await fireOutputPhase(classId, selectedIds, answers);
    setSubmitting(false);
  };

  // ------------------------------------------------------------------
  // Navigation
  // ------------------------------------------------------------------
  const captureCurrentStage = () => ({
    stageId,
    type: 'mcq',
    questions,
    answers,
    correctness,
    excerpt,
  });

  const handleOption = (option) => {
    if (option.toLowerCase().includes('revise')) {
      // Capture current stage results and switch to session summary
      setSessionLog(prev => [...prev, captureCurrentStage()]);
      setPhase('session_summary');
    } else if (nextStageId) {
      // Advancing to next stage — capture current stage first
      setSessionLog(prev => [...prev, captureCurrentStage()]);
      setStageId(nextStageId);
    }
  };

  const allAnswered = questions.length > 0 &&
    questions.every(q => {
      const ans = answers[q.resource_id];
      const isOE = q.resource_type === 'short_qa' || q.resource_type === 'open_ended';
      return isOE ? (ans || '').trim().length > 0 : !!ans;
    });

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (phase === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.badge}>Concept Check</span>
        </div>
        <p style={styles.loadingText}>Loading scaffold questions…</p>
      </div>
    );
  }

  if (phase === 'no_scaffold_needed') {
    return null;  // condition stage decided no scaffolding is needed
  }

  if (phase === 'error') {
    return (
      <div style={styles.container}>
        <p style={{ color: '#ef4444', fontSize: 13 }}>{error}</p>
      </div>
    );
  }

  if (phase === 'auto_generating') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{ ...styles.badge, background: '#0ea5e9' }}>Feedback</span>
          <span style={styles.stageLabel}>{stageId.replace('_', ' ')}</span>
        </div>
        <p style={styles.loadingText}>Analysing your responses and generating feedback…</p>
      </div>
    );
  }

  if (phase === 'no_questions') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.badge}>Concept Check</span>
          <span style={styles.stageLabel}>{stageId.replace('_', ' ')}</span>
        </div>
        <p style={styles.instruction}>
          No practice questions are available for this stage.
        </p>
        {nextOptions.length > 0 && (
          <div style={styles.nextOptions}>
            <p style={styles.nextLabel}>What would you like to do next?</p>
            {nextOptions.map(opt => (
              <button
                key={opt}
                onClick={() => handleOption(opt)}
                style={styles.optionBtn}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Session summary — shown when student clicks "Revise my ERD" at any point
  if (phase === 'session_summary') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{ ...styles.badge, background: '#16a34a' }}>Session Summary</span>
          <span style={styles.stageLabel}>Your progress this session</span>
        </div>

        {sessionLog.length === 0 ? (
          <p style={styles.instruction}>No completed stages yet.</p>
        ) : (
          sessionLog.map((entry, idx) => (
            <div key={idx}>
              <p style={styles.summaryStageLabel}>{entry.stageId.replace(/_/g, ' ')}</p>

              {entry.type === 'qa_interaction' ? (
                // Q&A turns
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {entry.chatHistory.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: 12, margin: 0 }}>No questions asked.</p>
                  ) : entry.chatHistory.map((msg, i) => (
                    <div key={i} style={msg.role === 'student' ? styles.chatMsgStudent : styles.chatMsgAI}>
                      <span style={styles.chatSender}>{msg.role === 'student' ? 'You' : 'Tutor'}</span>
                      <SimpleMarkdown>{msg.text}</SimpleMarkdown>
                    </div>
                  ))}
                </div>
              ) : (
                // MCQ / open_ended stage
                <>
                  {(entry.questions || []).map(q => {
                    const isOE = q.resource_type === 'short_qa' || q.resource_type === 'open_ended';
                    const isCorrect = entry.correctness?.[q.resource_id];
                    const studentAns = entry.answers?.[q.resource_id];
                    return (
                      <div key={q.resource_id} style={styles.summaryQuestionCard}>
                        <p style={styles.questionText}>{q.question}</p>
                        {isOE ? (
                          <p style={{ color: '#64748b', fontSize: 12, margin: 0 }}>
                            Your answer: {studentAns || '—'}
                          </p>
                        ) : (
                          <p style={{ margin: 0, fontSize: 13 }}>
                            Your answer: <strong>{studentAns || '—'}</strong>
                            {isCorrect !== undefined && (
                              <span style={{ color: isCorrect ? '#16a34a' : '#dc2626', marginLeft: 8, fontWeight: 600 }}>
                                {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {entry.excerpt && (
                    <div style={styles.excerptBox}>
                      <SimpleMarkdown>{entry.excerpt}</SimpleMarkdown>
                    </div>
                  )}
                </>
              )}

              {idx < sessionLog.length - 1 && (
                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />
              )}
            </div>
          ))
        )}

      </div>
    );
  }

  // Q&A chat phase
  if (phase === 'qa_chat') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{ ...styles.badge, background: '#0891b2' }}>Q&amp;A</span>
          <span style={styles.stageLabel}>{stageId.replace(/_/g, ' ')}</span>
        </div>

        {displayMessage && (
          <p style={{ ...styles.instruction, marginBottom: 10 }}>{displayMessage}</p>
        )}

        {/* Chat messages */}
        <div style={styles.chatBox}>
          {chatHistory.length === 0 && (
            <p style={styles.chatEmpty}>Ask your question below. The tutor will guide your thinking.</p>
          )}
          {chatHistory.map((msg, i) => (
            <div key={i} style={msg.role === 'student' ? styles.chatMsgStudent : styles.chatMsgAI}>
              <span style={styles.chatSender}>{msg.role === 'student' ? 'You' : 'Tutor'}</span>
              <SimpleMarkdown>{msg.text}</SimpleMarkdown>
            </div>
          ))}
          {qaSending && (
            <div style={styles.chatMsgAI}>
              <span style={styles.chatSender}>Tutor</span>
              <p style={{ fontStyle: 'italic', color: '#64748b', margin: 0 }}>Thinking…</p>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input row */}
        <div style={styles.chatInputRow}>
          <textarea
            value={qaInput}
            onChange={e => setQaInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQASend(); } }}
            placeholder="Ask a conceptual question… (Enter to send)"
            rows={2}
            disabled={qaSending}
            style={styles.chatInput}
          />
          <button
            onClick={handleQASend}
            disabled={!qaInput.trim() || qaSending}
            style={{
              ...styles.chatSendBtn,
              opacity: !qaInput.trim() || qaSending ? 0.5 : 1,
              cursor: !qaInput.trim() || qaSending ? 'not-allowed' : 'pointer',
            }}
          >
            Send
          </button>
        </div>

        {/* Revise — same style as next-options buttons, with extra top margin so it's not clicked accidentally */}
        <div style={styles.nextOptions}>
          <p style={styles.nextLabel}>When you're ready:</p>
          <button
            onClick={handleQARevise}
            disabled={submitting}
            style={{ ...styles.optionBtn, opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
          >
            {submitting ? 'Saving…' : 'Revise my ERD'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.badge}>Concept Check</span>
        <span style={styles.stageLabel}>{stageId.replace('_', ' ')}</span>
      </div>

      {/* Display message */}
      {displayMessage && (
        <p style={styles.instruction}>{displayMessage}</p>
      )}

      {/* Questions */}
      {questions.map(q => {
        const isOpenEnded = q.resource_type === 'short_qa' || q.resource_type === 'open_ended';
        const showCorrectness = phase === 'submitted' && !isOpenEnded && correctness[q.resource_id] !== undefined;
        return (
          <div key={q.resource_id}>
            {isOpenEnded ? (
              <OpenEndedQuestion
                question={q}
                answer={answers[q.resource_id] || ''}
                onChange={(id, val) => setAnswers(prev => ({ ...prev, [id]: val }))}
                disabled={phase === 'submitted'}
              />
            ) : (
              <MCQQuestion
                question={q}
                answer={answers[q.resource_id] || ''}
                onChange={(id, val) => setAnswers(prev => ({ ...prev, [id]: val }))}
                disabled={phase === 'submitted'}
              />
            )}
            {showCorrectness && (
              <div style={{ marginTop: -6, marginBottom: 8, paddingLeft: 4 }}>
                <CorrectnessIndicator resourceId={q.resource_id} correctness={correctness} />
              </div>
            )}
          </div>
        );
      })}

      {/* Submit button */}
      {phase === 'display' && (
        <button
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
          style={{
            ...styles.submitBtn,
            opacity: !allAnswered || submitting ? 0.5 : 1,
            cursor: !allAnswered || submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Checking…' : 'Submit Answers'}
        </button>
      )}

      {/* Adaptive excerpt */}
      {phase === 'submitted' && excerpt && (
        <div style={styles.excerptBox}>
          <SimpleMarkdown>{excerpt}</SimpleMarkdown>
        </div>
      )}

      {/* Next options */}
      {phase === 'submitted' && nextOptions.length > 0 && (
        <div style={styles.nextOptions}>
          <p style={styles.nextLabel}>What would you like to do next?</p>
          {nextOptions.map(opt => (
            <button
              key={opt}
              onClick={() => handleOption(opt)}
              style={styles.optionBtn}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  container: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '14px 16px',
    marginBottom: 12,
    fontSize: 13,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  badge: {
    background: '#4f46e5',
    color: '#fff',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
  stageLabel: {
    color: '#64748b',
    fontSize: 11,
    textTransform: 'capitalize',
  },
  instruction: {
    color: '#374151',
    marginBottom: 12,
    lineHeight: 1.5,
  },
  loadingText: {
    color: '#64748b',
    fontStyle: 'italic',
  },
  questionCard: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 10,
  },
  questionText: {
    fontWeight: 500,
    marginBottom: 10,
    lineHeight: 1.5,
    color: '#1e293b',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
    color: '#374151',
    lineHeight: 1.4,
  },
  openEndedTextarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    padding: '8px 10px',
    fontSize: 13,
    lineHeight: 1.5,
    color: '#1e293b',
    resize: 'vertical',
    marginTop: 6,
    fontFamily: 'inherit',
  },
  submitBtn: {
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 500,
    marginTop: 4,
    width: '100%',
  },
  excerptBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 6,
    padding: '10px 12px',
    marginTop: 12,
    lineHeight: 1.6,
    color: '#1e3a5f',
  },
  nextOptions: {
    marginTop: 14,
  },
  nextLabel: {
    color: '#374151',
    fontWeight: 500,
    marginBottom: 8,
  },
  optionBtn: {
    display: 'block',
    width: '100%',
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '7px 12px',
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'left',
    marginBottom: 6,
    color: '#1e293b',
  },
  chatBox: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '8px 10px',
    maxHeight: 260,
    overflowY: 'auto',
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  chatEmpty: {
    color: '#94a3b8',
    fontStyle: 'italic',
    margin: 0,
    fontSize: 12,
  },
  chatMsgStudent: {
    background: '#eff6ff',
    borderRadius: 6,
    padding: '6px 10px',
    alignSelf: 'flex-end',
    maxWidth: '90%',
  },
  chatMsgAI: {
    background: '#f0fdf4',
    borderRadius: 6,
    padding: '6px 10px',
    alignSelf: 'flex-start',
    maxWidth: '95%',
  },
  chatSender: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 2,
  },
  chatInputRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'flex-end',
  },
  chatInput: {
    flex: 1,
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    padding: '6px 8px',
    fontSize: 13,
    fontFamily: 'inherit',
    resize: 'none',
    lineHeight: 1.4,
  },
  chatSendBtn: {
    background: '#0891b2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  summaryStageLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 8,
    marginTop: 0,
  },
  summaryQuestionCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '8px 10px',
    marginBottom: 8,
  },
};
